import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const ErrorDashboard = () => {
  const [errors, setErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedError, setSelectedError] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const navigate = useNavigate();

  const loadErrors = () => {
    setIsLoading(true);
    try {
      const storedErrors = JSON.parse(localStorage.getItem('sccErrors') || '[]');
      setErrors(storedErrors);
      toast.success(`Loaded ${storedErrors.length} error(s)`);
    } catch (error) {
      toast.error('Failed to load errors');
      console.error('Error loading errors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearErrors = () => {
    localStorage.setItem('sccErrors', JSON.stringify([]));
    setErrors([]);
    toast.info('All errors cleared');
    loadErrors();

  };

  const exportErrors = () => {
    const dataStr = JSON.stringify(errors, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sccErrors.json';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported errors to JSON');
  };

  useEffect(() => {
    // Remember user signed-in state in localStorage
    const signedIn = localStorage.getItem('devAuthenticated') === 'true';
    if (!signedIn) {
      navigate('/_scc-internal-dev-system/login');
      return;
    }
    // Mark user as remembered for error dashboard
    localStorage.setItem('errorDashboardSignedIn', 'true');
    loadErrors();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Error Dashboard</h1>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <button
              onClick={loadErrors}
              disabled={isLoading}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? 'Loading...' : 'Refresh Errors'}
            </button>
            <button
              onClick={clearErrors}
              disabled={errors.length === 0}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md disabled:bg-red-400 disabled:cursor-not-allowed transition-colors"
            >
              Clear All Errors
            </button>
            <button
              onClick={exportErrors}
              disabled={errors.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
            >
              Export JSON
            </button>
            <span className="ml-4 text-green-700 font-semibold">{localStorage.getItem('errorDashboardSignedIn') === 'true' ? 'Signed In' : 'Not Signed In'}</span>
          </div>
        </div>

        {/* Analytics */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="bg-white rounded shadow p-4 flex-1">
            <b>Total Errors:</b> {errors.length}
            <br/>
            <b>Recent Error:</b> {errors[0]?.message || 'None'}
            <br/>
            <b>Unique Types:</b> {Array.from(new Set(errors.map(e => e.message))).length}
          </div>
          <div className="bg-white rounded shadow p-4 flex-1">
            <input
              type="text"
              placeholder="Search error message/date..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full p-2 border rounded"
            />
          </div>
        </div>

        {/* Error Modal */}
        {selectedError && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full">
              <h2 className="text-xl font-bold mb-4">Error Details</h2>
              <div className="mb-2"><b>Timestamp:</b> {new Date(selectedError.timestamp).toLocaleString()}</div>
              <div className="mb-2"><b>Message:</b> {selectedError.message}</div>
              <div className="mb-2"><b>Stack:</b> <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">{selectedError.stack}</pre></div>
              <div className="mb-2"><b>Context:</b> <pre className="bg-gray-100 p-2 rounded text-xs overflow-x-auto">{JSON.stringify(selectedError.context, null, 2)}</pre></div>
              <button className="mt-4 px-4 py-2 bg-gray-700 text-white rounded" onClick={() => setSelectedError(null)}>Close</button>
            </div>
          </div>
        )}

        {/* Pagination/filter/search logic */}
        {(() => {
          let filtered = errors;
          if (search) {
            filtered = errors.filter(e =>
              (e.message && e.message.toLowerCase().includes(search.toLowerCase())) ||
              (e.timestamp && new Date(e.timestamp).toLocaleString().includes(search))
            );
          }
          const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
          const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
          return (
            <>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                {paged.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paged.map((error, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(error.timestamp).toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600 max-w-xs truncate">
                              {error.message}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <button className="text-blue-600 hover:text-blue-800 underline" onClick={() => setSelectedError(error)}>View Details</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    No errors found in the log
                  </div>
                )}
              </div>
              {/* Pagination controls */}
              <div className="flex justify-center items-center gap-2 mt-4">
                <button disabled={page === 1} onClick={() => setPage(page - 1)} className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50">Prev</button>
                <span>Page {page} / {totalPages}</span>
                <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50">Next</button>
              </div>
            </>
          );
        })()}

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {errors.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {errors.map((error, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(error.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-600 max-w-xs truncate">
                        {error.message}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <details className="cursor-pointer">
                          <summary className="text-blue-600 hover:text-blue-800">View Details</summary>
                          <pre className="mt-2 p-2 bg-gray-100 rounded-md overflow-x-auto text-xs">
                            {JSON.stringify(error.context, null, 2)}
                          </pre>
                        </details>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              No errors found in the log
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ErrorDashboard;